// Neu (SPEC 03 §7.2.1, §7.2.4) — die vier Hauptziele, EINE Liste für Kopfleiste und Reiterleiste.
// Geändert (E32): fünftes Ziel „Karte“ — nur in der Kopfleiste (`mobil: false`): die Reiterleiste
// des Telefons trägt schon fünf Einträge (mit „Suchen“), ein sechster wäre bei 412 px zu schmal
// für „Auswertung“. Am Telefon führen „Suchen“, die Karten-Kachel und das Ortsverzeichnis hin.
import type { Ich } from "../../api/typen";
import { hatRecht } from "../../lib/rechte";

export interface NavZiel {
  id: "auswertung" | "karte" | "pruefen" | "themen" | "exporte";
  text: string;
  icon: string;
  zu: string;
  /** Pfadpräfixe, unter denen das Ziel leuchtet. */
  an: RegExp;
  /** false: nicht in der Reiterleiste des Telefons (Vorgabe: dort auch) */
  mobil?: boolean;
  /** true: in der Kopfleiste unter 1440 px nur das Symbol (Platz neben den vier Hauptzielen) */
  schmal?: boolean;
}

export function navZiele(V: string | null, ich: Ich | undefined): NavZiel[] {
  const b = V ? `/v/${encodeURIComponent(V)}` : null;
  const pruefenZiel = b ? (hatRecht(ich, V, "pruefen") ? `${b}/pruefen` : `${b}/lauf`) : "/verfahren";
  return [
    { id: "auswertung", text: "Auswertung", icon: "chart-bar", zu: b ? `${b}/auswerten` : "/verfahren", an: /^\/v\/[^/]+\/auswerten/ },
    // E32: nur mit gewähltem Verfahren (die Karte gehört zu einem); Recht lesen wie die Auswertung.
    ...(b && hatRecht(ich, V, "lesen")
      ? [{ id: "karte" as const, text: "Karte", icon: "map-trifold", zu: `${b}/karte`, an: /^\/v\/[^/]+\/karte/, mobil: false, schmal: true }]
      : []),
    // Prüfen leuchtet auch auf Eingang und Lauf (Reiter von Prüfen, DESIGN §3).
    { id: "pruefen", text: "Prüfen", icon: "check-square-offset", zu: pruefenZiel, an: /^\/v\/[^/]+\/(pruefen|eingang|lauf)/ },
    { id: "themen", text: "Themen", icon: "tag", zu: b ? `${b}/themen` : "/verfahren", an: /^\/v\/[^/]+\/themen/ },
    { id: "exporte", text: "Exporte", icon: "export", zu: b ? `${b}/exporte` : "/verfahren", an: /^(\/v\/[^/]+\/exporte|\/x\/)/ },
  ];
}

/** Fragebar fokussieren — auf der Auswertung sofort, sonst nach dem Wechsel dorthin. */
export function fragebarFokussieren(): boolean {
  const el = document.querySelector<HTMLInputElement>("[data-fragebar] input, .fragebar input");
  if (!el) return false;
  el.focus();
  el.select?.();
  return true;
}

export function fragebarSpaeterFokussieren(): void {
  let versuche = 0;
  const probe = () => {
    if (fragebarFokussieren() || ++versuche > 25) return;
    setTimeout(probe, 60);
  };
  setTimeout(probe, 30);
}

/** Steht die Fragebar gerade im Bild (Auswertung des aktuellen Verfahrens)? */
export function fragebarDa(): boolean {
  return !!document.querySelector("[data-fragebar] input, .fragebar input");
}

/* Vorbelegung der Fragebar aus der Befehlssuche („In Antworten suchen: …“). Der Text lebt NUR in
   diesem Modul — nie im Browserspeicher (S1), nie in einer Adresse (S7). Die Fragebar holt ihn ab:
   sofort, wenn sie schon steht, sonst beim Einhängen nach dem Wechsel zur Auswertung. Was nach
   15 s niemand abgeholt hat, verfällt (sonst tauchte er Stunden später in einer Fragebar auf). */
let vorbelegung: { text: string; t: number } | null = null;
const vorbelegungsHoerer = new Set<() => void>();
const VORBELEGUNG_MS = 15_000;

export function fragebarVorbelegen(text: string): void {
  vorbelegung = { text, t: Date.now() };
  for (const h of [...vorbelegungsHoerer]) h();
}

/** Die Vorbelegung abholen (genau einmal) — null, wenn keine (frische) da ist. */
export function fragebarVorbelegungNehmen(): string | null {
  const v = vorbelegung;
  vorbelegung = null;
  return v && Date.now() - v.t <= VORBELEGUNG_MS ? v.text : null;
}

export function fragebarVorbelegungAbonnieren(h: () => void): () => void {
  vorbelegungsHoerer.add(h);
  return () => void vorbelegungsHoerer.delete(h);
}

/**
 * Vorauswahl der Kopfleiste: ist noch kein (gültiges) Verfahren gewählt und gibt es GENAU eines,
 * dann dieses — sonst zeigte die Pille „—“, obwohl es nichts zu wählen gibt. Bei zwei und mehr
 * bleibt die Wahl beim Menschen.
 */
export function einzigesVerfahren(codes: readonly string[], gewaehlt: string | null): string | null {
  if (gewaehlt && codes.includes(gewaehlt)) return null;
  return codes.length === 1 ? codes[0]! : null;
}
